import { useState, useEffect } from 'react'
import { AlertTriangle, Check } from 'lucide-react'
import { Button, Input } from '@/components/ui'

export interface BillingLimitsSectionProps {
  capUsd: number | null | undefined
  currentSpendUsd?: number
  isLoading: boolean
  isSaving: boolean
  onSave: (capUsd: number | null) => Promise<void>
  isEditing?: boolean
  onEditChange?: (editing: boolean) => void
  hideNoCap?: boolean
}

export function BillingLimitsSection({ capUsd, currentSpendUsd, isLoading, isSaving, onSave, isEditing = false, onEditChange, hideNoCap = false }: BillingLimitsSectionProps) {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (capUsd === undefined) return
    setInputValue(capUsd !== null ? String(capUsd) : '')
  }, [capUsd])

  const noCapDefined = capUsd === null || capUsd === undefined
  const isZeroCap = capUsd === 0

  const handleCancel = () => {
    onEditChange?.(false)
    setInputValue(capUsd !== null && capUsd !== undefined ? String(capUsd) : '')
    setError(null)
  }

  const handleSave = async () => {
    const trimmed = inputValue.trim()
    if (trimmed === '') {
      setError('Spending cap is required.')
      return
    }
    setError(null)
    await onSave(Number(trimmed))
    onEditChange?.(false)
  }

  const currentSpendLabel = noCapDefined ? 'no cap set' : `$${capUsd!.toFixed(2)} cap`
  const capDisplayValue = noCapDefined ? '$ –' : `$${capUsd!.toFixed(2)}`

  return (
    <div className="space-y-4">

      {!isLoading && noCapDefined && !hideNoCap && (
        <div
          role="status"
          className="flex w-fit items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          data-testid="spending-cap-no-cap-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-semibold">No spending cap defined.</span>
            <br />
            <span>All Claude workflows in this project are currently blocked. Set a cap to enable them.</span>
          </div>
        </div>
      )}

      {!isLoading && isZeroCap && (
        <div
          role="alert"
          className="flex w-fit items-start gap-2 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="spending-cap-zero-cap-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Spending cap is set to $0. All Claude workflows in this project are blocked.</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium" htmlFor="spending-cap-input">
            Spending cap (USD)
          </label>
          {isEditing ? (
            <>
              <Input
                id="spending-cap-input"
                data-testid="spending-cap-input"
                type="text"
                inputMode="decimal"
                placeholder="$ e.g. 100"
                value={inputValue}
                onChange={e => {
                  const val = e.target.value
                  if (val === '' || /^\d{0,16}(\.\d{0,2})?$/.test(val)) {
                    setInputValue(val)
                    setError(null)
                  }
                }}
                disabled={isSaving}
              />
              {error && (
                <p role="alert" className="text-xs text-destructive" data-testid="spending-cap-error">
                  {error}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Enter 0 to explicitly set a $0 cap (block all workflows).
              </p>
            </>
          ) : (
            <p className="text-sm py-2 text-muted-foreground" data-testid="spending-cap-display">
              {capDisplayValue}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium">Current period spend</p>
          {isEditing ? (
            <Input
              value={`$${(currentSpendUsd ?? 0).toFixed(2)} of ${currentSpendLabel}`}
              readOnly
              disabled
              data-testid="spending-cap-current-spend"
            />
          ) : (
            <p className="text-sm py-2 text-muted-foreground" data-testid="spending-cap-current-spend">
              {`$${(currentSpendUsd ?? 0).toFixed(2)} of ${currentSpendLabel}`}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium">Billing period start</p>
          {isEditing ? (
            <Input
              value="1st of every month"
              readOnly
              disabled
            />
          ) : (
            <p className="text-sm py-2 text-muted-foreground">1st of every month</p>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isSaving}>
            <Check />
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  )
}
